/**
 * TimelineRenderer — Time-based visualization for tasks and events (DRR-178)
 *
 * Groups items by time blocks (morning/afternoon/evening/night or by hour)
 * with a horizontal timeline showing scheduled vs actual times.
 *
 * Used when scope dimension `time` (field: due_date) is selected with
 * suggested_renderer: timeline.
 *
 * Domain-agnostic: works for care tasks, construction shifts, accounting periods.
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Card,
  SimpleGrid,
  ThemeIcon,
  Progress,
  ScrollArea,
} from '@mantine/core';
import {
  IconSun,
  IconSunrise,
  IconMoon,
  IconSunset,
  IconClock,
} from '@tabler/icons-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimelineItem {
  id: string;
  name: string;
  scheduledStart?: string;
  dueAt?: string;
  status?: string;
  priority?: string;
  safetyLevel?: string;
  taskGroupRef?: string;
  subjectActorId?: string;
}

interface TimeBlock {
  id: string;
  label: string;
  icon: React.ReactNode;
  startHour: number;
  endHour: number;
  color: string;
  items: TimelineItem[];
}

interface TimelineRendererProps<T> {
  items: T[];
  /** Field to extract scheduled time from */
  timeField?: string;
  /** Adapter to convert raw item to TimelineItem */
  fieldAdapter: (item: T) => TimelineItem;
  /** Click handler */
  onItemClick?: (item: T) => void;
  /** Max height */
  maxHeight?: string;
}

// ---------------------------------------------------------------------------
// Time blocks
// ---------------------------------------------------------------------------

const TIME_BLOCKS: Omit<TimeBlock, 'items'>[] = [
  { id: 'morning', label: 'Morning (6AM-12PM)', icon: <IconSunrise size={18} />, startHour: 6, endHour: 12, color: 'yellow' },
  { id: 'afternoon', label: 'Afternoon (12PM-5PM)', icon: <IconSun size={18} />, startHour: 12, endHour: 17, color: 'orange' },
  { id: 'evening', label: 'Evening (5PM-10PM)', icon: <IconSunset size={18} />, startHour: 17, endHour: 22, color: 'grape' },
  { id: 'night', label: 'Night (10PM-6AM)', icon: <IconMoon size={18} />, startHour: 22, endHour: 6, color: 'blue' },
];

function getTimeBlock(timeStr: string | undefined): string {
  if (!timeStr) return 'morning'; // default
  try {
    const date = new Date(timeStr);
    const hour = date.getHours();
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 22) return 'evening';
    return 'night';
  } catch {
    return 'morning';
  }
}

// Also try parsing "HH:MM" schedule format from task groups
function parseScheduleHour(schedule: string | undefined): number | null {
  if (!schedule) return null;
  const match = schedule.match(/^(\d{1,2}):(\d{2})$/);
  if (match && match[1]) return parseInt(match[1], 10);
  return null;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'gray',
  in_progress: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'dimmed',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  normal: 'blue',
  low: 'gray',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TimelineRenderer<T>({
  items,
  fieldAdapter,
  onItemClick,
  maxHeight = '600px',
}: TimelineRendererProps<T>) {
  const timeBlocks = useMemo(() => {
    const adapted = items.map((item, idx) => ({
      raw: item,
      timeline: fieldAdapter(item),
      index: idx,
    }));

    const blocks: TimeBlock[] = TIME_BLOCKS.map((block) => ({
      ...block,
      items: adapted
        .filter((a) => {
          const time = a.timeline.scheduledStart ?? a.timeline.dueAt;
          // Try schedule-format "HH:MM" first
          const hour = parseScheduleHour(time as string | undefined);
          if (hour !== null) {
            if (block.id === 'night') return hour >= 22 || hour < 6;
            return hour >= block.startHour && hour < block.endHour;
          }
          return getTimeBlock(time) === block.id;
        })
        .map((a) => a.timeline),
    }));

    return blocks;
  }, [items, fieldAdapter]);

  const totalItems = items.length;
  const completedItems = items.filter(
    (item) => fieldAdapter(item).status === 'completed',
  ).length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <ScrollArea style={{ maxHeight }}>
      <Stack gap="md" p="xs">
        {/* Progress bar */}
        <Card withBorder p="xs" radius="md">
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={600}>
              Today&apos;s Progress
            </Text>
            <Text size="xs" c="dimmed">
              {completedItems} / {totalItems} ({progressPercent}%)
            </Text>
          </Group>
          <Progress value={progressPercent} size="lg" radius="xl" color="teal" />
        </Card>

        {/* Time blocks */}
        {timeBlocks.map((block) => (
          <Card key={block.id} withBorder p="sm" radius="md">
            <Group gap="sm" mb="xs">
              <ThemeIcon variant="light" color={block.color} size="sm">
                {block.icon}
              </ThemeIcon>
              <Text size="sm" fw={600}>
                {block.label}
              </Text>
              <Badge size="xs" variant="light" color={block.color}>
                {block.items.length}
              </Badge>
            </Group>

            {block.items.length === 0 ? (
              <Text size="xs" c="dimmed" ml="lg">
                No tasks scheduled
              </Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xs">
                {block.items.map((item) => (
                  <Card
                    key={item.id}
                    withBorder
                    p="xs"
                    radius="sm"
                    style={{ cursor: onItemClick ? 'pointer' : undefined }}
                    onClick={() => {
                      if (onItemClick) {
                        const raw = items.find(
                          (i) => fieldAdapter(i).id === item.id,
                        );
                        if (raw) onItemClick(raw);
                      }
                    }}
                  >
                    <Group justify="space-between" gap="xs">
                      <Text size="xs" fw={500} lineClamp={1} style={{ flex: 1 }}>
                        {item.name}
                      </Text>
                      <Badge
                        size="xs"
                        variant="dot"
                        color={STATUS_COLORS[item.status ?? ''] ?? 'gray'}
                      >
                        {item.status ?? 'pending'}
                      </Badge>
                    </Group>
                    <Group gap={4} mt={2}>
                      {item.priority && (
                        <Badge size="xs" variant="light" color={PRIORITY_COLORS[item.priority] ?? 'gray'}>
                          {item.priority}
                        </Badge>
                      )}
                      {item.scheduledStart && (
                        <Group gap={2}>
                          <IconClock size={10} />
                          <Text size="xs" c="dimmed">
                            {item.scheduledStart}
                          </Text>
                        </Group>
                      )}
                    </Group>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Card>
        ))}
      </Stack>
    </ScrollArea>
  );
}
